// Ava's mark: the Gap Center symbol alone, in one of her eight states. Ported from
// the brand board ava-states-v5.html (Notion, Brand and Design, Ava's states and
// motion). The company lockup lives in `components/brand` and never moves; this
// mark appears in content, next to a line of text that says what Ava is doing.
//
// Rules carried into code: arms use `currentColor` so they read ink on light and
// paper on dark; sizes at or below 24 px use the small-size drawing (the brand
// guidelines' threshold; the board's own code switched at 34); only Working and
// Watching loop, and only when `animate` is set; reduced motion shows the still
// pose (see styles/ava-mark.css). Needs you and Done may instead `play` their
// motion once, when the state arrives, and then hold. The caller keeps to one
// looping mark per screen and the wording of the state beside it.
export type AvaState =
  | 'watching'
  | 'working'
  | 'unsure'
  | 'needs'
  | 'done'
  | 'waiting'
  | 'paused'
  | 'blocked';

// Master drawing and the small-size drawing (shorter arms, bigger center).
const MASTER_ARM = 'M50 6Q61 6 61 17V25Q61 36 50 36Q39 36 39 25V17Q39 6 50 6Z';
const SMALL_ARM = 'M50 2Q61 2 61 13V21Q61 33 50 33Q39 33 39 21V13Q39 2 50 2Z';
// Still arm travel in canvas units. Working reaches 9, Not sure holds at 5,
// Paused settles 2 inward, Blocked holds at 3.
const REST: Record<AvaState, readonly [number, number, number, number]> = {
  watching: [0, 0, 0, 0],
  working: [-9, 0, 0, 0],
  unsure: [-5, -5, -5, -5],
  needs: [0, 0, 0, 0],
  done: [0, 0, 0, 0],
  waiting: [0, 0, 0, 0],
  paused: [2, 2, 2, 2],
  blocked: [-3, -3, -3, -3],
};
const YELLOW = 'var(--ava-yellow)';
const TOMATO = 'var(--needs-you)';
const STONE = 'var(--stone)';
const INK = 'var(--ink)';
const LOOPING: ReadonlySet<AvaState> = new Set(['working', 'watching']);

const PLAYS_ONCE: ReadonlySet<AvaState> = new Set(['needs', 'done', 'unsure']);

function Center({ state, play }: { state: AvaState; play: boolean }) {
  switch (state) {
    case 'unsure':
      // Ten small separate dots on a radius 10 circle, never a chain.
      return (
        <circle
          className="ava-unsure-ring"
          cx="50"
          cy="50"
          r="10"
          fill="none"
          stroke={YELLOW}
          strokeWidth="2.6"
          strokeDasharray="0 6.283"
          strokeLinecap="round"
        />
      );
    case 'needs':
      return (
        <>
          {play && (
            <circle
              className="ava-ping"
              cx="50"
              cy="50"
              r="10"
              fill="none"
              stroke={TOMATO}
              strokeWidth="2"
            />
          )}
          <circle
            cx="50"
            cy="50"
            r="15"
            fill="none"
            stroke={TOMATO}
            strokeWidth="1.6"
            opacity=".55"
          />
          <circle className="ava-center" cx="50" cy="50" r="10" fill={TOMATO} />
        </>
      );
    case 'done':
      return (
        <>
          <circle className="ava-center" cx="50" cy="50" r="10" fill={YELLOW} />
          <path
            className={play ? 'ava-check' : undefined}
            d="M45 50.5L48.6 54L55.4 46.6"
            pathLength={20}
            fill="none"
            stroke={INK}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'waiting':
      // The center at half size. A radius, not a transform: the CSS transform-origin
      // on `.ava-center` would otherwise compound with an attribute transform.
      return <circle className="ava-center" cx="50" cy="50" r="5" fill={YELLOW} />;
    case 'paused':
      return (
        <>
          <circle className="ava-center" cx="50" cy="50" r="10" fill={YELLOW} />
          <g fill={INK}>
            <rect x="45.6" y="45" width="3" height="10" rx="1" />
            <rect x="51.4" y="45" width="3" height="10" rx="1" />
          </g>
        </>
      );
    case 'blocked':
      return (
        <>
          <circle className="ava-center" cx="50" cy="50" r="10" fill={STONE} />
          <rect x="44.5" y="48.6" width="11" height="2.8" rx="1.2" fill="var(--background)" />
        </>
      );
    default:
      return <circle className="ava-center" cx="50" cy="50" r="10" fill={YELLOW} />;
  }
}

export function AvaMark({
  state,
  size = 28,
  animate = false,
  play = false,
  className,
}: {
  state: AvaState;
  size?: number;
  /** Loop the state's motion. Only Working and Watching loop; other states stay still. */
  animate?: boolean;
  /** Play Needs you, Done or Not sure once, then hold. */
  play?: boolean;
  className?: string;
}) {
  const looping = animate && LOOPING.has(state);
  const playing = play && PLAYS_ONCE.has(state);
  const small = size <= 24 && !looping;
  const arm = small ? SMALL_ARM : MASTER_ARM;
  const rest = REST[state];
  const center = <Center state={state} play={playing} />;
  return (
    <svg
      className={['ava-mark', className].filter(Boolean).join(' ')}
      data-state={state}
      data-animate={looping ? state : undefined}
      data-play={playing ? state : undefined}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
    >
      {rest.map((travel, index) => {
        // While looping, the keyframes own the pose; the still pose is set in CSS
        // for reduced motion. Still marks carry their pose as attributes.
        const offset = small ? Math.round(travel * 0.6) : travel;
        const still = !looping && travel !== 0 ? `translate(0 ${offset})` : undefined;
        const working = !looping && state === 'working' && index === 0;
        return (
          <g key={index} transform={`rotate(${index * 90} 50 50)`}>
            <g
              className={`ava-arm ava-arm-${index}`}
              transform={still}
              style={working ? { color: YELLOW } : undefined}
            >
              <path d={arm} fill="currentColor" />
            </g>
          </g>
        );
      })}
      {small ? <g transform="translate(50 50) scale(1.45) translate(-50 -50)">{center}</g> : center}
    </svg>
  );
}
